import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { resolveAvatar } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, ProgressBar, StatChip } from "@/components/shared";
import * as Icons from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["dash-student"], queryFn: async () => (await api.get("/dashboard/student")).data });
  if (isLoading || !data) return <Loader />;
  const s = data.stats;

  return (
    <div className="space-y-6" data-testid="student-home">
      {/* Hero */}
      <motion.div initial={{ scale: 0.97, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="relative overflow-hidden rounded-3xl gradient-purple p-6 text-white sm:p-8">
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10" />
        <div className="absolute -bottom-20 right-24 h-40 w-40 rounded-full bg-csh-orange/30" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-white/70 font-semibold">{data.program?.name}</p>
            <h1 className="font-head text-4xl sm:text-5xl">Welcome back, {user?.name?.split(" ")[0]}! 👋</h1>
            <div className="mt-3 flex items-center gap-2 text-sm font-bold">
              <span className="rounded-full bg-white/15 px-3 py-1">Level {s.level_index + 1} · {s.level_name}</span>
              {s.streak > 0 && <span className="rounded-full bg-csh-orange px-3 py-1 flex items-center gap-1"><Icons.Flame size={14} /> {s.streak} day streak</span>}
            </div>
          </div>
          <img src={resolveAvatar(user?.avatar)} alt="" className="h-20 w-20 rounded-2xl object-cover ring-4 ring-white/30" />
        </div>
        <div className="relative mt-6">
          <div className="mb-1 flex justify-between text-sm font-bold">
            <span>Journey — Week {data.current_week} of {data.total_weeks}</span>
            <span>{data.journey_pct}%</span>
          </div>
          <ProgressBar pct={data.journey_pct} />
        </div>
      </motion.div>

      {/* Stat chips */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatChip testid="chip-points" icon="Star" value={s.points} label="Story Points" color="#EAB308" />
        <StatChip testid="chip-badges" icon="Medal" value={s.badges_count} label="Badges" color="#662D91" />
        <StatChip testid="chip-quests" icon="Target" value={s.quests_done} label="Quests Done" color="#FC633B" />
        <StatChip testid="chip-attendance" icon="CalendarCheck" value={`${s.attendance_pct}%`} label="Attendance" color="#22C55E" />
      </div>

      {/* Level progress */}
      <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-head text-xl text-csh-blue">Level Progress</h3>
          <span className="text-sm font-bold text-muted-foreground">
            {s.next_level_name ? `${s.xp_to_next} XP to ${s.next_level_name}` : "Max Level! 🏆"}
          </span>
        </div>
        <ProgressBar pct={s.level_progress_pct} />
      </div>

      {/* Next mission + next class */}
      <div className="grid gap-4 md:grid-cols-2">
        {data.next_quest && (
          <motion.div whileHover={{ y: -4 }} className="cursor-pointer rounded-3xl bg-white p-6 shadow-sm border-2 border-csh-orange/30 card-pop"
            data-testid="next-quest-card" onClick={() => navigate(`/app/quests/${data.next_quest.id}`)}>
            <div className="flex items-center gap-2 text-csh-orange font-bold uppercase text-sm"><Icons.Target size={18} /> Next Mission</div>
            <h3 className="mt-2 font-head text-2xl text-csh-blue">Quest #{data.next_quest.number} — {data.next_quest.name}</h3>
            <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{data.next_quest.instructions}</p>
            <Button className="mt-4 rounded-full gradient-orange text-white hover:scale-105">Start Quest <Icons.ArrowRight size={16} /></Button>
          </motion.div>
        )}
        {data.next_session && (
          <motion.div whileHover={{ y: -4 }} className="cursor-pointer rounded-3xl bg-white p-6 shadow-sm border-2 border-csh-blue/20 card-pop"
            data-testid="next-session-card" onClick={() => navigate(`/app/session/${data.next_session.id}`)}>
            <div className="flex items-center gap-2 text-csh-blue font-bold uppercase text-sm"><Icons.Video size={18} /> Next Class</div>
            <h3 className="mt-2 font-head text-2xl text-csh-blue">{data.next_session.title}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{data.next_session.date} · {data.next_session.time}</p>
            <Button className="mt-4 rounded-full gradient-blue text-white hover:scale-105">View Session <Icons.ArrowRight size={16} /></Button>
          </motion.div>
        )}
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
        {[["Map", "Journey", "/app/journey"], ["Clapperboard", "Replays", "/app/replays"], ["Medal", "Badges", "/app/badges"],
          ["Mic", "Stories", "/app/stories"], ["Trophy", "Ranks", "/app/leaderboard"], ["Award", "Finale", "/app/performance"]].map(([ic, lb, to]) => {
          const I = Icons[ic];
          return (
            <button key={to} onClick={() => navigate(to)} data-testid={`quick-${lb.toLowerCase()}`}
              className="flex flex-col items-center gap-2 rounded-2xl bg-white p-4 shadow-sm border border-black/5 hover:border-csh-purple transition-colors">
              <I size={24} className="text-csh-purple" /> <span className="text-xs font-bold text-csh-grey">{lb}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
